import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Form, Button, Modal, Alert, InputGroup } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import Breadcrumb from '../components/Breadcrumb';
import apiService from '../services/apiService';
import Swal from 'sweetalert2';
import './test.css';

const ManageManagers = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [createForm, setCreateForm] = useState({
    name: '',
    email: '',
    base_commission: 20, // percentage input
  });
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  const [showEdit, setShowEdit] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', base_commission: 0 });
  const [savingEdit, setSavingEdit] = useState(false);

  const breadcrumbItems = [
    { label: 'Commissions', href: null },
    { label: 'Management', href: null },
    { label: 'Managers', href: null },
  ];

  const loadItems = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getManagersManage();
      setItems(Array.isArray(data) ? data : (data.results || []));
    } catch (e) {
      setError(e.message || 'Failed to load managers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadItems(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      setCreating(true);
      const payload = {
        name: createForm.name,
        email: createForm.email || null,
        base_commission: Number(createForm.base_commission) / 100,
      };
      await apiService.createManager(payload);
      setCreateForm({ name: '', email: '', base_commission: 20 });
      setShowCreate(false);
      await loadItems();
    } catch (e) {
      setError(e.message || 'Failed to create manager');
    } finally {
      setCreating(false);
    }
  };

  const openEdit = (item) => {
    setEditItem(item);
    setEditForm({
      name: item.name || '',
      email: item.email || '',
      base_commission: typeof item.base_commission === 'number' ? (item.base_commission * 100) : 0,
    });
    setShowEdit(true);
  };

  const handleSaveEdit = async () => {
    if (!editItem) return;
    try {
      setSavingEdit(true);
      const payload = {
        name: editForm.name,
        email: editForm.email || null,
        base_commission: Number(editForm.base_commission) / 100,
      };
      await apiService.updateManager(editItem.id, payload);
      setShowEdit(false);
      setEditItem(null);
      await loadItems();
    } catch (e) {
      setError(e.message || 'Failed to update manager');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async (item) => {
    const result = await Swal.fire({
      title: `Delete ${item.name}?`,
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Delete',
    });
    if (!result.isConfirmed) return;
    try {
      await apiService.deleteManager(item.id);
      await Swal.fire({ title: 'Deleted', text: `${item.name} removed successfully.`, icon: 'success', timer: 1200, showConfirmButton: false });
      await loadItems();
    } catch (e) {
      setError(e.message || 'Failed to delete manager');
    }
  };

  return (
    <div className="py-3">
      <Breadcrumb items={breadcrumbItems} />

      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="page-title mb-1">Managers</h2>
              <p className="page-subtitle text-muted mb-0">Manage manager records</p>
            </div>
            <div className="d-flex align-items-center gap-3">
              <div className="btn-group" role="group">
                <Button
                  variant={showCreate ? 'primary' : 'outline-primary'}
                  className="d-flex align-items-center gap-2"
                  onClick={() => setShowCreate(true)}
                >
                  <i className="fas fa-plus"></i> New Manager
                </Button>
              </div>
            </div>
          </div>
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          {loading ? (
            <div className="text-muted small">Loading...</div>
          ) : items.length === 0 ? (
            <div className="text-muted small">No managers available.</div>
          ) : (
            <div className="containesr py-2">
              <div className="row g-3">
                {items.map((m, index) => (
                  <div className="col-md-6 col-lg-3" key={m.id || index}>
                    <div className="agent-card p-3">
                      <div className="d-flex justify-content-between align-items-center mb-4">
                        <div className="d-flex align-items-center gap-3">
                          <div className="avatar-gradient">
                            <i className="fas fa-fw fa-user-tie text-white fs-5"></i>
                          </div>
                          <div>
                            <h5 className="fw-semibold mb-0 d-flex align-items-center gap-2">
                              {m.name}
                            </h5>
                            {m.email && (
                              <p className="text-muted small mb-0">{m.email}</p>
                            )}
                          </div>
                        </div>

                        <div className="d-flex align-items-center gap-2">
                          <button
                            className="btn btn-light btn-sm edit-btn"
                            onClick={() => openEdit(m)}
                            aria-label={`Edit ${m.name}`}
                          >
                            <i className="fas fa-fw fa-pen"></i>
                          </button>
                        </div>
                      </div>

                      <hr className="my-2" />

                      <div className="card-body p-0">
                        <div className="commission-box">
                          <p className="small mb-1">Base Commission</p>
                          <p className="fw-bold fs-6 mb-0">
                            {typeof m.base_commission === 'number' ? `${(m.base_commission * 100).toFixed(2)}%` : m.base_commission}
                          </p>
                        </div>

                        <div className="mt-3 d-flex justify-content-end">
                          <button
                            className="btn btn-danger btn-sm delete-btn"
                            onClick={() => handleDelete(m)}
                            aria-label={`Delete ${m.name}`}
                          >
                            <i className="fas fa-fw fa-trash"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Col>
      </Row>

      {/* Create Modal */}
      <Modal show={showCreate} onHide={() => setShowCreate(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Create Manager</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleCreate} className="row g-3">
            <Form.Group as={Col} md={6} controlId="create_name">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                placeholder="Enter name"
                required
              />
            </Form.Group>
            <Form.Group as={Col} md={6} controlId="create_email">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                value={createForm.email}
                onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                placeholder="Enter email"
              />
            </Form.Group>
            <Form.Group as={Col} md={6} controlId="create_base_commission">
              <Form.Label>Base Commission</Form.Label>
              <InputGroup>
                <Form.Control
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={createForm.base_commission}
                  onChange={(e) => setCreateForm({ ...createForm, base_commission: e.target.value })}
                />
                <InputGroup.Text>%</InputGroup.Text>
              </InputGroup>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreate(false)}>Cancel</Button>
          <Button type="submit" variant="primary" onClick={handleCreate} disabled={creating}>
            {creating ? 'Saving...' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Modal */}
      <Modal show={showEdit} onHide={() => setShowEdit(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Manager</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form className="row g-3">
            <Form.Group as={Col} md={6} controlId="edit_name">
              <Form.Label>Name</Form.Label>
              <Form.Control type="text" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
            </Form.Group>
            <Form.Group as={Col} md={6} controlId="edit_email">
              <Form.Label>Email</Form.Label>
              <Form.Control type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
            </Form.Group>
            <Form.Group as={Col} md={6} controlId="edit_base_commission">
              <Form.Label>Base Commission</Form.Label>
              <InputGroup>
                <Form.Control type="number" step="0.01" min="0" max="100" value={editForm.base_commission} onChange={(e) => setEditForm({ ...editForm, base_commission: e.target.value })} />
                <InputGroup.Text>%</InputGroup.Text>
              </InputGroup>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEdit(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleSaveEdit} disabled={savingEdit}>
            {savingEdit ? 'Saving...' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ManageManagers;